import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  adminGetPerformer,
  adminUpdateStatus,
  adminAddNote,
  adminContactPerformer,
  adminDeletePerformer,
} from '../services/api';
import Navbar from '../components/Navbar';
import AdminTabBar from '../components/AdminTabBar';

const STATUS_OPTIONS = [
  { value: 'new',          label: 'New'          },
  { value: 'active',       label: 'Active'       },
  { value: 'contacted',    label: 'Contacted'    },
  { value: 'booked',       label: 'Booked'       },
  { value: 'not_suitable', label: 'Not Suitable' },
  { value: 'inactive',     label: 'Inactive'     },
];

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
      <span style={{ color: 'var(--muted)', minWidth: '150px', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text)' }}>{value}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="a-card" style={{ marginBottom: '1rem' }}>
      <div className="a-section-title">{title}</div>
      {children}
    </div>
  );
}

export default function AdminPerformerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  })();
  const isAdmin = ['admin', 'metaadmin'].includes(currentUser.role);

  const [performer, setPerformer] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  // Status panel
  const [statusValue,  setStatusValue]  = useState('');
  const [statusNote,   setStatusNote]   = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusMsg,    setStatusMsg]    = useState('');

  // Add note
  const [noteText,   setNoteText]   = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteMsg,    setNoteMsg]    = useState('');

  // Contact modal
  const [showContact,     setShowContact]     = useState(false);
  const [contactSubject,  setContactSubject]  = useState('');
  const [contactBody,     setContactBody]     = useState('');
  const [contactSending,  setContactSending]  = useState(false);
  const [contactMsg,      setContactMsg]      = useState('');

  // Delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    adminGetPerformer(id)
      .then(res => {
        setPerformer(res.data);
        setStatusValue(res.data.status);
      })
      .catch(() => setError('Failed to load performer.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusSave = async () => {
    setStatusSaving(true); setStatusMsg('');
    try {
      await adminUpdateStatus(id, { status: statusValue, note: statusNote });
      setStatusMsg('Status updated.');
      setStatusNote('');
      load();
    } catch {
      setStatusMsg('Failed to update status.');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setNoteSaving(true); setNoteMsg('');
    try {
      await adminAddNote(id, { note: noteText });
      setNoteMsg('Note added.');
      setNoteText('');
      load();
    } catch {
      setNoteMsg('Failed to add note.');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleContact = async () => {
    if (!contactSubject.trim() || !contactBody.trim()) return;
    setContactSending(true); setContactMsg('');
    try {
      await adminContactPerformer(id, { subject: contactSubject, body: contactBody });
      setContactMsg('Email sent.');
      setTimeout(() => { setShowContact(false); setContactMsg(''); setContactSubject(''); setContactBody(''); load(); }, 1500);
    } catch {
      setContactMsg('Failed to send email.');
    } finally {
      setContactSending(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminDeletePerformer(id);
      navigate('/admin');
    } catch {
      alert('Delete failed. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  const fmtDMY = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return `${String(dt.getUTCDate()).padStart(2,'0')}/${String(dt.getUTCMonth()+1).padStart(2,'0')}/${dt.getUTCFullYear()}`;
  };

  if (loading) return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>
    </>
  );

  if (error || !performer) return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ padding: '2rem' }}>
        <div className="a-error">{error || 'Performer not found.'}</div>
        <Link to="/admin" className="a-btn a-btn--ghost a-btn--sm" style={{ marginTop: '1rem' }}>Back</Link>
      </div>
    </>
  );

  const p = performer;

  return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem' }}>
          <button onClick={() => navigate('/admin')} className="a-btn a-btn--ghost a-btn--sm" style={{ flexShrink: 0, marginTop: '4px' }}>
            ← Back
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
                {p.stage_name || p.real_name || 'Unnamed Performer'}
              </h1>
              <span className={`a-badge a-badge--${p.status}`}>{p.status}</span>
            </div>
            {p.stage_name && p.real_name && (
              <p style={{ margin: '0.2rem 0 0', color: 'var(--muted)', fontSize: '0.875rem' }}>{p.real_name}</p>
            )}
            <p style={{ margin: '0.2rem 0 0', color: 'var(--muted)', fontSize: '0.85rem' }}>{p.email}</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            {isAdmin && (
              <button
                className="a-btn a-btn--ghost a-btn--sm"
                onClick={() => setShowDeleteConfirm(true)}
                style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
              >
                Delete
              </button>
            )}
            {isAdmin && (
              <button
                className="a-btn a-btn--navy a-btn--sm"
                onClick={() => {
                  setContactSubject(`Regarding your SCALA Performer application`);
                  setContactBody(`Hi ${(p.stage_name || p.real_name || 'there').split(' ')[0]},\n\n`);
                  setShowContact(true);
                }}
              >
                Contact
              </button>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '1.25rem' }}>
          {(isAdmin ? ['profile', 'notes'] : ['profile']).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid var(--blue)' : '2px solid transparent',
                background: 'none',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '14px',
                color: activeTab === tab ? 'var(--blue)' : 'var(--muted)',
                marginBottom: '-1px',
                textTransform: 'capitalize',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ── PROFILE TAB ── */}
        {activeTab === 'profile' && (
          <div>
            {/* About */}
            <Section title="About">
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flexShrink: 0 }}>
                  {p.profile_photo_url ? (
                    <img src={p.profile_photo_url} alt="Profile" className="a-photo" style={{ width: '80px', height: '80px' }} />
                  ) : (
                    <div className="a-photo-placeholder" style={{ width: '80px', height: '80px' }}>
                      {(p.stage_name || p.real_name || '?')[0].toUpperCase()}
                    </div>
                  )}
                  {p.promo_photo_url && (
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginBottom: '0.25rem', textAlign: 'center' }}>Promo</div>
                      <img
                        src={p.promo_photo_url}
                        alt="Promo"
                        style={{ width: '80px', height: '52px', objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)', display: 'block' }}
                      />
                      <a
                        href={p.promo_photo_url}
                        download
                        target="_blank"
                        rel="noreferrer"
                        className="a-download-link"
                      >
                        Download image
                      </a>
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  {p.bio && <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', lineHeight: 1.5, color: 'var(--text)' }}>{p.bio}</p>}
                  <Row label="Phone"       value={p.phone} />
                  <Row label="Registered"  value={formatDate(p.created_at)} />
                  <Row label="SCALA Member" value={p.is_scala_member ? 'Yes' : 'No'} />
                </div>
              </div>
            </Section>

            {/* Performance */}
            <Section title="Performance">
              <Row label="Type" value={p.performance_type} />
              {p.performers_on_stage && <Row label="On stage" value={`${p.performers_on_stage} performer${p.performers_on_stage !== 1 ? 's' : ''}`} />}
              {p.genres && p.genres.length > 0 && (
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  {p.genres.map(g => <span key={g.id || g.genre_id} className="a-tag">{g.name}</span>)}
                </div>
              )}
              {(p.set_length_min || p.set_length_max) && (
                <Row label="Set Length" value={
                  p.set_length_min && p.set_length_max
                    ? `${p.set_length_min}–${p.set_length_max} min`
                    : p.set_length_min ? `From ${p.set_length_min} min` : `Up to ${p.set_length_max} min`
                } />
              )}
              {p.website          && <Row label="Website"   value={<a href={p.website}          target="_blank" rel="noreferrer">{p.website}</a>} />}
              {p.social_facebook  && <Row label="Facebook"  value={<a href={p.social_facebook}  target="_blank" rel="noreferrer">{p.social_facebook}</a>} />}
              {p.social_instagram && <Row label="Instagram" value={<a href={p.social_instagram} target="_blank" rel="noreferrer">{p.social_instagram}</a>} />}
              {p.social_youtube   && <Row label="YouTube"   value={<a href={p.social_youtube}   target="_blank" rel="noreferrer">{p.social_youtube}</a>} />}
              {p.social_tiktok    && <Row label="TikTok"    value={<a href={p.social_tiktok}    target="_blank" rel="noreferrer">{p.social_tiktok}</a>} />}
              {p.social_spotify   && <Row label="Spotify"   value={<a href={p.social_spotify}   target="_blank" rel="noreferrer">{p.social_spotify}</a>} />}
            </Section>

            {/* Media */}
            {p.media && p.media.length > 0 && (
              <Section title="Media">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {p.media.map((m, i) => (
                    <a key={i} href={m.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.875rem', color: 'var(--blue)' }}>
                      {m.title || m.url}
                    </a>
                  ))}
                </div>
              </Section>
            )}

            {/* Availability */}
            <Section title="Availability">
              {(() => {
                const avail = p.availability || [];
                const notes = p.availability_notes;
                if (avail.length === 0 && !notes) {
                  return <p style={{ fontSize: '0.9rem', color: 'var(--muted)', fontStyle: 'italic', margin: 0 }}>Not specified</p>;
                }
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {avail.map((a, i) => {
                      if (a.availability_type === 'always') return <div key={i} style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Usually available</div>;
                      if (a.availability_type === 'range') return <div key={i} style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Available {fmtDMY(a.start_date)} to {fmtDMY(a.end_date)}</div>;
                      if (a.availability_type === 'specific') return <div key={i} style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Available on {fmtDMY(a.start_date)}</div>;
                      return null;
                    })}
                    {notes && <div style={{ fontSize: '0.9rem', color: 'var(--muted)', marginTop: '0.25rem' }}>{notes}</div>}
                  </div>
                );
              })()}
            </Section>

            {/* Stage Requirements */}
            <Section title="Stage Requirements">
              {(() => {
                const vocalMics     = p.stage?.vocal_mics;
                const hasVocalMics  = vocalMics != null && vocalMics > 0;
                const hasInstruments = p.instruments && p.instruments.length > 0;
                if (!hasVocalMics && !hasInstruments) {
                  return <p style={{ fontSize: '0.9rem', color: 'var(--muted)', fontStyle: 'italic', margin: 0 }}>No stage requirements specified</p>;
                }
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {hasVocalMics && <div style={{ fontSize: '0.875rem', color: 'var(--text)' }}>Vocal microphones: {vocalMics}</div>}
                    {hasInstruments && p.instruments.map((inst, i) => {
                      const detail = inst.drum_type || inst.amplification;
                      return (
                        <div key={i} style={{ fontSize: '0.875rem', color: 'var(--text)' }}>
                          {inst.instrument_name}{detail ? ` — ${detail}` : ''}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </Section>
          </div>
        )}

        {/* ── NOTES TAB ── */}
        {activeTab === 'notes' && (
          <div style={{ maxWidth: '560px' }}>
            {/* Status — admin only */}
            {isAdmin && (
              <div className="a-card" style={{ marginBottom: '1rem' }}>
                <div className="a-section-title">Status</div>
                <select
                  className="a-select"
                  value={statusValue}
                  onChange={e => setStatusValue(e.target.value)}
                  style={{ marginBottom: '0.5rem' }}
                >
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <textarea
                  className="a-textarea"
                  placeholder="Optional note (will be added to history)"
                  value={statusNote}
                  onChange={e => setStatusNote(e.target.value)}
                  rows={2}
                  style={{ marginBottom: '0.5rem', resize: 'vertical' }}
                />
                {statusMsg && (
                  <div style={{ fontSize: '0.825rem', color: statusMsg.includes('Failed') ? 'var(--red)' : 'var(--green)', marginBottom: '0.5rem' }}>
                    {statusMsg}
                  </div>
                )}
                <button
                  className="a-btn a-btn--navy a-btn--full a-btn--sm"
                  onClick={handleStatusSave}
                  disabled={statusSaving}
                >
                  {statusSaving ? 'Saving…' : 'Update Status'}
                </button>
              </div>
            )}

            {/* Add Note */}
            <div className="a-card" style={{ marginBottom: '1rem' }}>
              <div className="a-section-title">Add Note</div>
              <textarea
                className="a-textarea"
                placeholder="Internal note…"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={3}
                style={{ marginBottom: '0.5rem', resize: 'vertical' }}
              />
              {noteMsg && (
                <div style={{ fontSize: '0.825rem', color: noteMsg.includes('Failed') ? 'var(--red)' : 'var(--green)', marginBottom: '0.5rem' }}>
                  {noteMsg}
                </div>
              )}
              <button
                className="a-btn a-btn--ghost a-btn--full a-btn--sm"
                onClick={handleAddNote}
                disabled={noteSaving || !noteText.trim()}
              >
                {noteSaving ? 'Saving…' : 'Add Note'}
              </button>
            </div>

            {/* Notes history */}
            {p.admin_notes && p.admin_notes.length > 0 && (
              <div className="a-card">
                <div className="a-section-title">Notes History</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {p.admin_notes.map((n, i) => {
                    const isSystem   = n.note_type === 'system';
                    const authorLabel = n.author_name || n.admin_email || 'Admin';
                    return (
                      <div key={n.note_id || n.id || i} style={{ borderLeft: `3px solid ${isSystem ? 'var(--border)' : 'var(--blue)'}`, paddingLeft: '0.75rem' }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.2rem' }}>
                          {authorLabel} · {formatDate(n.created_at)}
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: isSystem ? 'var(--muted)' : 'var(--text)',
                          fontStyle: isSystem ? 'italic' : 'normal',
                          whiteSpace: 'pre-wrap',
                        }}>
                          {n.note}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Contact Modal */}
      {showContact && (
        <div className="a-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowContact(false); }}>
          <div className="a-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
                Contact {p.stage_name || p.real_name}
              </h2>
              <button onClick={() => setShowContact(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
            </div>
            <div className="a-field">
              <label className="a-label">Subject</label>
              <input className="a-input" value={contactSubject} onChange={e => setContactSubject(e.target.value)} />
            </div>
            <div className="a-field">
              <label className="a-label">Message</label>
              <textarea className="a-textarea" value={contactBody} onChange={e => setContactBody(e.target.value)} rows={8} style={{ resize: 'vertical' }} />
            </div>
            {contactMsg && (
              <div style={{ fontSize: '0.875rem', color: contactMsg.includes('Failed') ? 'var(--red)' : 'var(--green)', marginBottom: '0.75rem' }}>
                {contactMsg}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setShowContact(false)}>Cancel</button>
              <button
                className="a-btn a-btn--navy"
                onClick={handleContact}
                disabled={contactSending || !contactSubject.trim() || !contactBody.trim()}
              >
                {contactSending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="a-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowDeleteConfirm(false); }}>
          <div className="a-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Delete Performer</h2>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
            </div>
            <p style={{ marginBottom: '1.25rem', color: 'var(--text)', fontSize: '0.9rem' }}>
              Are you sure you want to permanently delete <strong>{p.stage_name || p.real_name || p.email}</strong>?
              This will remove their profile, account, and all uploaded files. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Cancel
              </button>
              <button className="a-btn a-btn--danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
